/**
 * De spelregels van Memory, los van het scherm.
 *
 * Alles hier is een pure functie op een gewoon object, zodat de regels in
 * tests/memory.test.mjs zonder browser nagelopen kunnen worden. Het tekenen, de
 * klok en het uitkiezen van de krabbels staan in logic/memory.js.
 *
 * Twintig kaartjes, tien paren, en op elk kaartje een echte krabbel van de paal.
 * Alleen stilstaande en vierkante krabbels: een bewegende krabbel verraadt zich
 * doordat hij beweegt, en dan is het geen memory meer.
 */

/** Tien paren is twintig kaartjes: vijf kolommen van vier. */
export const PAREN = 10;

/** Onder de twee paren valt er niets te onthouden. */
export const PAREN_MIN = 2;

/**
 * Een lijst door de war, zonder de lijst zelf aan te raken.
 *
 * Fisher-Yates van achter naar voren: elke plek ruilt met een willekeurige plek
 * die nog niet aan de beurt was. Dat is de enige manier van schudden waarbij elke
 * volgorde even waarschijnlijk is; het bekendere sort() met een willekeurige
 * uitkomst is dat niet, en dat scheelt bij twaalf kaartjes echt.
 */
export function schud(lijst, rng = Math.random) {
  const uit = lijst.slice();
  for (let i = uit.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = uit[i]; uit[i] = uit[j]; uit[j] = t;
  }
  return uit;
}

/**
 * Een nieuw spel uit een lijst krabbels.
 *
 * `krabbels` zijn de krabbels waar je uit kunt kiezen, meer dan genoeg of net
 * genoeg. We schudden die lijst, pakken de eerste `paren` en zetten van elk twee
 * kaartjes neer; daarna gaat het hele stapeltje nog een keer door de war, anders
 * liggen de paren netjes naast elkaar.
 */
export function nieuwSpel(krabbels, paren = PAREN, rng = Math.random) {
  const kies = schud(krabbels, rng).slice(0, Math.max(PAREN_MIN, Math.min(paren, krabbels.length)));
  const stapel = schud(kies.concat(kies).map((k, i) => ({ nr: i, krabbel: k })), rng);
  return {
    kaarten: stapel,
    // De plekken die nu open liggen, hoogstens twee.
    open: [],
    // De plekken die al gevonden zijn en open blijven liggen.
    gevonden: [],
    zetten: 0,
    klaar: false
  };
}

/** Ligt dit kaartje open of is het al gevonden? */
export function zichtbaar(spel, i) {
  return spel.open.indexOf(i) >= 0 || spel.gevonden.indexOf(i) >= 0;
}

/** Liggen er twee open die niet bij elkaar horen? Dan moeten ze weer dicht. */
export function misser(spel) {
  return spel.open.length === 2 && spel.kaarten[spel.open[0]].krabbel !== spel.kaarten[spel.open[1]].krabbel;
}

/**
 * Een kaartje omdraaien.
 *
 * Niks doen als het spel klaar is, als er al twee open liggen, of als je op een
 * kaartje tikt dat al zichtbaar is. Dat laatste is geen fout van jou, dus het
 * kost ook geen zet.
 *
 * Draai je de tweede om, dan is dat één zet. Horen ze bij elkaar, dan gaan ze
 * meteen naar de gevonden stapel; zo niet, dan blijven ze open liggen tot
 * sluit() ze omdraait. Dat wachten hoort niet hier: dat is een klok, en die
 * staat in logic/memory.js.
 */
export function draai(spel, i) {
  if (spel.klaar) return spel;
  if (spel.open.length >= 2) return spel;
  if (i < 0 || i >= spel.kaarten.length) return spel;
  if (zichtbaar(spel, i)) return spel;

  const open = spel.open.concat([i]);
  if (open.length < 2) return { ...spel, open };

  const zetten = spel.zetten + 1;
  const [a, b] = open;
  if (spel.kaarten[a].krabbel !== spel.kaarten[b].krabbel) return { ...spel, open, zetten };

  const gevonden = spel.gevonden.concat(open);
  return { ...spel, open: [], gevonden, zetten, klaar: gevonden.length === spel.kaarten.length };
}

/** De twee missers weer dichtdoen. */
export function sluit(spel) {
  return spel.open.length === 2 ? { ...spel, open: [] } : spel;
}

/** Hoeveel paren er nog liggen. */
export function teGaan(spel) {
  return (spel.kaarten.length - spel.gevonden.length) / 2;
}
